<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Icon from '@/Components/Utils/Icon.vue'
import Tag from '@/Components/Utils/Tag.vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import { useFormatTime } from '@/Composables/useFormatTime'

interface Stat {
    label: string
    value: number
    type?: string
}

defineProps<{
    title?: string
    pageHead: PageHeadingTS
    stats: Record<string, Stat>
    state_breakdown: { label: string, value: string, icon: any, color: string, count: number }[]
    ongoing_orders: any[]
}>()

const formatStat = (stat: Stat) => stat.type === 'currency' ? useFormatCurrency(stat.value) : useFormatNumber(stat.value)
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="px-4 py-4">
        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div v-for="(stat, key) in stats" :key="key" class="rounded-xl border border-gray-200 bg-white p-4">
                <div class="text-xs text-gray-400">{{ stat.label }}</div>
                <div class="mt-1 text-xl font-semibold">{{ formatStat(stat) }}</div>
            </div>
        </div>

        <div class="mt-4 grid gap-4 lg:grid-cols-4">
            <Link
                v-for="state in state_breakdown"
                :key="state.value"
                :href="route('kasir.orders.index', { 'filter[state]': state.value })"
                class="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-4 hover:bg-gray-50"
            >
                <div class="flex items-center gap-x-2">
                    <Icon :data="state.icon" />
                    <span class="text-sm text-gray-600">{{ state.label }}</span>
                </div>
                <span class="text-lg font-semibold" :style="{ color: state.color }">{{ state.count }}</span>
            </Link>
        </div>

        <section class="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div class="border-b border-gray-100 bg-gray-50 px-6 py-3 font-semibold text-gray-700">
                Pesanan aktif
            </div>

            <div v-if="!ongoing_orders.length" class="px-6 py-8 text-center text-sm text-gray-400">
                Tidak ada pesanan yang sedang berjalan
            </div>

            <ul v-else class="divide-y divide-gray-100">
                <li v-for="order in ongoing_orders" :key="order.slug" class="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
                    <div class="min-w-0">
                        <Link :href="route('kasir.orders.show', order.slug)" class="primaryLink font-mono text-xs">
                            {{ order.reference }}
                        </Link>
                        <div class="font-medium">{{ order.brand_name }}</div>
                        <div class="text-xs text-gray-400">
                            Estimasi selesai: {{ useFormatTime(order.estimated_finished_at) }}
                        </div>
                    </div>

                    <div class="flex items-center gap-x-4">
                        <Tag v-if="order.needs_manufacture" label="Perlu produksi" :theme="16" noHoverColor />
                        <div class="flex items-center gap-x-1.5 text-sm">
                            <Icon :data="order.state_icon" />
                            <span>{{ order.state_label }}</span>
                        </div>
                        <div class="text-right">
                            <div class="font-medium">{{ useFormatCurrency(order.total_amount) }}</div>
                            <div v-if="order.remaining_amount > 0" class="text-xs text-red-500">
                                sisa {{ useFormatCurrency(order.remaining_amount) }}
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </section>
    </div>
</template>
